/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

import axios from '../../services/axios';
import Loading from '../../components/Loading';
import CurrencyCell from '../../components/MinorUtilities/CurrencyCell';

// DS (mesmo padrão do ProductEdit)
import { Container } from '../../styles/layout/Container';

import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';

import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../styles/layout/ActionBar';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';

import PaginationBar from '../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';

export default function AdvancedProductSearch() {
   const [categories, setCategories] = useState([]);
   const [brands, setBrands] = useState([]);
   const [pdvs, setPdvs] = useState([]);

   const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

   const [selectedPdvName, setSelectedPdvName] = useState('TODOS');

   const [products, setProducts] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   const [currentPage, setCurrentPage] = useState(1);
   const itemsPerPage = 15;

   const [query, setQuery] = useState({});

   useEffect(() => {
      async function getBaseLists() {
         try {
            setIsLoading(true);

            const [categoriesRes, brandsRes, pdvsRes] = await Promise.all([
               axios.get('/prodcategories'),
               axios.get('/prodbrands'),
               axios.get('/pdvs'),
            ]);

            setCategories(categoriesRes.data || []);
            setBrands(brandsRes.data || []);
            setPdvs(pdvsRes.data || []);

            setIsLoading(false);
         } catch (err) {
            const errors = get(err, 'response.data.errors', []);
            errors.forEach((error) => toast.error(error));
            setIsLoading(false);
         }
      }

      getBaseLists();
   }, []);

   const fetchProducts = async (
      sortBy = sortConfig.key ?? 'qtd',
      sortDirection = sortConfig.direction === 'none'
         ? 'desc'
         : sortConfig.direction ?? 'desc'
   ) => {
      try {
         setIsLoading(true);

         const payload = {
            ...query,
            sort_by: sortBy,
            sort_direction: sortDirection,
         };

         const response = await axios.post('/advancedstocksearch', payload);

         const data = response?.data || [];
         setProducts(data);
         setCurrentPage(1);

         if (query.pdv_id) {
            const selectedPdv = pdvs.find(
               (pdv) => pdv.id === parseInt(query.pdv_id, 10)
            );
            setSelectedPdvName(selectedPdv ? selectedPdv.pdv_name : 'TODOS');
         } else {
            setSelectedPdvName('TODOS');
         }
      } catch (err) {
         const errors = get(err, 'response.data.errors', []);
         errors.forEach((error) => toast.error(error));
      } finally {
         setIsLoading(false);
      }
   };

   const handleGetProducts = (e) => {
      e.preventDefault();
      setSortConfig({ key: null, direction: null });
      fetchProducts();
   };

   const handleChangeParams = (e) => {
      const { name, value } = e.target;

      setQuery((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const handleSort = (key) => {
      let newKey = key;
      let newDirection = 'asc';

      if (sortConfig.key === key) {
         if (sortConfig.direction === 'asc') newDirection = 'desc';
         else {
            newKey = null;
            newDirection = 'none';
         }
      }

      setSortConfig({ key: newKey, direction: newDirection });
      fetchProducts(
         newKey ?? 'qtd',
         newDirection === 'none' ? 'desc' : newDirection
      );
   };

   const getSortIcon = (key) => {
      if (sortConfig.key !== key) return ' ⇅';
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
   };

   const handleExportExcel = () => {
      if (!products || products.length === 0) {
         toast.warn('Nenhum dado para exportar.');
         return;
      }

      const dataForSheet = products.map((p) => ({
         ID: p.id,
         SKU: p.prod_sku,
         'Nome produto': p.prod_name,
         'Estoque mín.': p.min_stock,
         'Preço de custo': p.cost_price,
         Qtd: p.qtd,
         'Valor total': p.total_value,
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dataForSheet);

      XLSX.utils.book_append_sheet(wb, ws, 'estoque_avancado');

      const fileName = `estoque_avancado_${selectedPdvName || 'TODOS'}.xlsx`;
      XLSX.writeFile(wb, fileName);
   };

   const totalPages = Math.ceil(products.length / itemsPerPage);
   const currentProducts = useMemo(() => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      return products.slice(startIndex, startIndex + itemsPerPage);
   }, [products, currentPage]);

   const totalQtd = useMemo(
      () => products.reduce((acc, p) => acc + (Number(p.qtd) || 0), 0),
      [products]
   );

   const totalValue = useMemo(
      () => products.reduce((acc, p) => acc + (Number(p.total_value) || 0), 0),
      [products]
   );

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Pesquisa avançada de estoque</PageTitle>
               <PageSubtitle>
                  Consulte de forma detalhada a situação do estoque por PDV,
                  marca e categoria.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleGetProducts}>
                  <FormGrid>
                     <Field $col={4} $colMobile={12}>
                        <label>Ponto de venda</label>
                        <select
                           name="pdv_id"
                           value={query.pdv_id || ''}
                           onChange={handleChangeParams}
                        >
                           <option value="">Todos</option>
                           {pdvs.map((pdv) => (
                              <option key={String(pdv.id)} value={pdv.id}>
                                 {pdv.pdv_name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Marca</label>
                        <select
                           name="brand_id"
                           value={query.brand_id || ''}
                           onChange={handleChangeParams}
                        >
                           <option value="">Todas</option>
                           {brands.map((brand) => (
                              <option key={String(brand.id)} value={brand.id}>
                                 {brand.name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>Categoria</label>
                        <select
                           name="category_id"
                           value={query.category_id || ''}
                           onChange={handleChangeParams}
                        >
                           <option value="">Todas</option>
                           {categories.map((category) => (
                              <option
                                 key={String(category.id)}
                                 value={category.id}
                              >
                                 {category.name}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={12} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button type="submit" disabled={isLoading}>
                                 Pesquisar
                              </button>

                              <button
                                 type="button"
                                 onClick={handleExportExcel}
                                 disabled={isLoading || products.length === 0}
                              >
                                 Exportar Excel
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Resultados</SectionTitle>

                  <SectionMeta
                     items={[
                        {
                           key: 'pdv',
                           label: 'PDV',
                           value: selectedPdvName,
                        },
                        {
                           key: 'count',
                           label: 'Itens',
                           value: products.length,
                        },
                        {
                           key: 'page',
                           label: 'Página',
                           value: `${currentPage}/${totalPages || 1}`,
                        },
                     ]}
                  />
               </SectionHeaderRow>
            </SectionHeader>

            <SectionBody>
               {currentProducts.length ? (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th style={{ width: '5%' }}>ID</th>
                                 <th style={{ width: '10%' }}>SKU</th>
                                 <th
                                    style={{ width: '40%', cursor: 'pointer' }}
                                    onClick={() => handleSort('prod_name')}
                                 >
                                    Nome do produto{getSortIcon('prod_name')}
                                 </th>
                                 <th style={{ width: '10%' }}>Estoque mín.</th>
                                 <th style={{ width: '10%' }}>
                                    Preço de custo
                                 </th>
                                 <th
                                    style={{ width: '10%', cursor: 'pointer' }}
                                    onClick={() => handleSort('qtd')}
                                 >
                                    Qtd{getSortIcon('qtd')}
                                 </th>
                                 <th
                                    style={{ width: '15%', cursor: 'pointer' }}
                                    onClick={() => handleSort('total_value')}
                                 >
                                    Valor total{getSortIcon('total_value')}
                                 </th>
                              </tr>
                           </thead>

                           <tbody>
                              {currentProducts.map((p) => (
                                 <tr
                                    key={String(p.id)}
                                    onDoubleClick={() => {
                                       window.location.href = `/products/${p.id}`;
                                    }}
                                    title="Dê duplo clique para abrir o produto"
                                    style={{ cursor: 'pointer' }}
                                 >
                                    <td>{p.id}</td>
                                    <td>{p.prod_sku}</td>
                                    <td>{p.prod_name}</td>
                                    <td style={{ textAlign: 'center' }}>
                                       {p.min_stock}
                                    </td>
                                    <CurrencyCell value={p.cost_price} />
                                    <td style={{ textAlign: 'center' }}>
                                       {p.qtd}
                                    </td>
                                    <CurrencyCell value={p.total_value} />
                                 </tr>
                              ))}
                           </tbody>

                           <tfoot>
                              <tr>
                                 <td colSpan={5} style={{ textAlign: 'right' }}>
                                    <strong>Totais:</strong>
                                 </td>
                                 <td style={{ textAlign: 'center' }}>
                                    <strong>{totalQtd}</strong>
                                 </td>
                                 <td style={{ textAlign: 'right' }}>
                                    <strong>
                                       {Number(totalValue).toLocaleString(
                                          'pt-BR',
                                          {
                                             style: 'currency',
                                             currency: 'BRL',
                                          }
                                       )}
                                    </strong>
                                 </td>
                              </tr>
                           </tfoot>
                        </Table>
                     </TableWrapper>

                     <ActionBar>
                        <PaginationBar
                           currentPage={currentPage}
                           totalPages={totalPages}
                           onPageChange={setCurrentPage}
                           disabled={isLoading || totalPages <= 1}
                        />
                     </ActionBar>
                  </>
               ) : (
                  <EmptyState
                     title="Nenhum resultado"
                     description="Selecione os filtros e clique em “Pesquisar” para exibir o estoque."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
